/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import { PanelLeftOpen } from 'lucide-react';
import { useRef } from 'react';
import { z } from 'zod';
import { ReviewEmptyState } from '@/app/(client)/questionnaires/-components/ReviewEmptyState';
import { ReviewMainHeader } from '@/app/(client)/questionnaires/-components/ReviewMainHeader';
import { ReviewMainPanel } from '@/app/(client)/questionnaires/-components/ReviewMainPanel';
import { ReviewOutline } from '@/app/(client)/questionnaires/-components/ReviewOutline';
import { ReviewSideList } from '@/app/(client)/questionnaires/-components/ReviewSideList';
import { usePreviewNavigation } from '@/app/(client)/questionnaires/-hooks/usePreviewNavigation';
import { useReviewMainPanel } from '@/app/(client)/questionnaires/-hooks/useReviewMainPanel';
import { useReviewRoute } from '@/app/(client)/questionnaires/-hooks/useReviewRoute';
import { isAnswerFilled } from '@/app/(client)/questionnaires/-types';
import { IconButton } from '@/components/shared/IconButton';
import { useTranslator } from '@/hooks/useTranslator';
import { cn } from '@/lib/utils/cn';

const reviewSearchSchema = z.object({
  /** Selected company's request id — set when the user clicks a row
   *  in the side list (or arrives via deep link from the Summary
   *  subtab's companies table). */
  requestId: z.string().optional(),
});

export const Route = createFileRoute(
  '/(client)/questionnaires/(pages)/(detail)/$id/review/',
)({
  validateSearch: search => reviewSearchSchema.parse(search),
  component: ResponsesReviewRoute,
});

function ResponsesReviewRoute() {
  const { id: questionnaireId } = Route.useParams();
  const { requestId } = Route.useSearch();
  const { getTranslation: t } = useTranslator({ page: 'questionnaires' });
  const r = useReviewRoute({ questionnaireId, requestId });
  // Panel state is owned here (not in ReviewMainPanel) so its toolbar
  // controls can live in the full-width header while the sections
  // render in the body below.
  const panel = useReviewMainPanel({ request: r.request });

  // Scroll-spy for the left content menu — observes the questions scroll
  // container and drives the active-section highlight + click-to-scroll.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const nav = usePreviewNavigation({
    sections: r.request?.sections ?? [],
    scrollContainerRef,
  });

  // Which answers are still missing — drives the completion donut and
  // the red flags in the content menu.
  const reviewQuestions = r.request?.questions ?? [];
  const unsubmittedQuestionIds = new Set(
    reviewQuestions
      .filter(q => !isAnswerFilled(r.answers.getValue(q.questionId)))
      .map(q => q.questionId),
  );
  const totalQuestions = reviewQuestions.length;
  const filledQuestions = totalQuestions - unsubmittedQuestionIds.size;

  return (
    // Single page scroll: the header scrolls away while the content menu
    // and picker stay pinned (sticky) and the answers flow. The scroll
    // container is the observer root for the outline scroll-spy.
    <div
      ref={scrollContainerRef}
      className='flex flex-col flex-1 min-h-0 overflow-y-auto'
    >
      {/* Full-width company detail header — spans the whole page above
          the selector + questions body. Only shown once a company is
          selected. */}
      {r.request !== undefined && (
        <div className='px-4 pt-5 min-[1600px]:px-6 min-[1600px]:pt-8'>
          <ReviewMainHeader
            request={r.request}
            onStatusChange={r.handleStatusChange}
            isSubmitting={r.isSubmitting}
            onBack={r.clearSelection}
            onOpenSync={panel.openSync}
            syncDisabled={panel.syncableQuestions.length === 0}
            {...(r.logoUrl !== undefined ? { logoUrl: r.logoUrl } : {})}
          />
        </div>
      )}

      <div className='flex items-start gap-8 px-4 pt-4 pb-4 min-[1600px]:px-6 min-[1600px]:pt-6 min-[1600px]:pb-6'>
        {/* Left: content menu — sticky so it stays while answers scroll. */}
        {r.request !== undefined && (
          <aside className='hidden @5xl:flex @5xl:w-72 @5xl:shrink-0 flex-col @5xl:sticky @5xl:top-4 @5xl:max-h-[calc(100dvh-7rem)] overflow-y-auto'>
            <ReviewOutline
              sections={r.request.sections}
              questions={r.request.questions}
              activeSectionId={nav.activeSectionId}
              onSelectSection={nav.scrollToSection}
              onSelectQuestion={nav.scrollToQuestion}
              numberFor={qid => panel.numberByQuestionId.get(qid) ?? 0}
              unsubmittedQuestionIds={unsubmittedQuestionIds}
              filled={filledQuestions}
              total={totalQuestions}
            />
          </aside>
        )}

        {/* Center: the answer cards — flow in the page scroll. */}
        <div
          className={cn(
            'flex-1 min-w-0',
            r.hasSelection ? '' : 'hidden @5xl:block',
          )}
        >
          {r.request !== undefined ? (
            <ReviewMainPanel
              request={r.request}
              panel={panel}
              getValue={r.answers.getValue}
              setValue={r.answers.setValue}
              {...(r.onApproveQuestion !== undefined
                ? { onApproveQuestion: r.onApproveQuestion }
                : {})}
              {...(r.onDenyQuestion !== undefined
                ? { onDenyQuestion: r.onDenyQuestion }
                : {})}
            />
          ) : (
            <ReviewEmptyState />
          )}
        </div>

        {/* Right: the company picker — sticky with its own bounded scroll
            (the list virtualizes, so it needs a bounded height). */}
        {r.sideListOpen && (
          <div
            className={cn(
              'flex-col w-full @5xl:w-80 @5xl:shrink-0 @5xl:sticky @5xl:top-4 max-h-[calc(100dvh-7rem)] overflow-hidden',
              r.hasSelection ? 'hidden @5xl:flex' : 'flex',
            )}
          >
            <ReviewSideList
              requests={r.filteredRequests}
              total={r.totalRequests}
              {...(requestId !== undefined
                ? { selectedRequestId: requestId }
                : {})}
              onSelectRequest={r.selectRequest}
              isLoading={r.listIsLoading}
              filters={r.filters}
              onFiltersChange={r.setFilters}
              bulkScopeIds={r.bulkScopeIds}
              onBulkToggle={r.toggleBulkScope}
              allFilteredSelected={r.allFilteredSelected}
              someFilteredSelected={r.someFilteredSelected}
              onToggleSelectAll={r.toggleSelectAll}
              bulkActions={r.bulkActions}
              onInviteCompanies={r.openInvite}
              canInvite={r.canInvite}
              companyLogosById={r.networkCompaniesById}
              onCollapse={r.closeSideList}
              hasNextPage={r.hasNextPage}
              isFetchingNextPage={r.isFetchingNextPage}
              onFetchNextPage={r.fetchNextPage}
            />
          </div>
        )}

        {!r.sideListOpen && (
          <div className='hidden @5xl:flex shrink-0 items-start rounded-lg border border-border bg-card @5xl:sticky @5xl:top-4'>
            <IconButton
              variant='ghost'
              size='icon-sm'
              tooltip={t('responses.review.sideList.expand')}
              onClick={r.openSideList}
              className='m-2 text-muted-foreground hover:text-foreground'
            >
              <PanelLeftOpen />
            </IconButton>
          </div>
        )}
      </div>
    </div>
  );
}
